<template>
  <div class="nebMonth">
    <div class="nebMonth-header">
      <div class="nebMonth-header-prev">
        <neb-icon v-show="!hidePrevious"
          name="caretLeft"
          height="10px"
          width="10px"
          @click.native="$emit('previousMonth')"
        ></neb-icon>
      </div>

      <div class="nebMonth-header-monthName">
        <span>{{ `${humanizedMonth} ${month === -1 ? year - 1 : year}` }}</span>
      </div>

      <div class="nebMonth-header-next">
        <neb-icon v-show="!hideNext"
          name="caretRight"
          height="10px"
          widthh="10px"
          @click.native="$emit('nextMonth')"
        ></neb-icon>
      </div>
    </div>

    <div class="nebMonth-dayNames">
      <div class="nebMonth-dayNames-name"
        v-for="name in dayNames"
        :key="name"
      >
        <span>{{ name }}</span>
      </div>
    </div>

    <div class="nebMonth-days">
      <div class="nebMonth-days-day"
        v-for="day in availableDays"
        :key="day"
        :class="[
          {
            filler: !day,
            disabled: dayIsDisabled(day)
          }
        ].concat(classForDay(day))"
        @click="() => onDayClick(day)"
      >
        <div>{{ day }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { toNumber, isNumber, isFinite } from 'lodash'

import NebIcon from '../../Icon'

const dayNames = [
  'SUN', 'MON', 'TUE',
  'WED', 'THU', 'FRI',
  'SAT'
]

export default {
  name: 'NebMonth',

  components: {
    NebIcon
  },

  props: {
    year: {
      type: Number,
      default: 0
    },
    month: {
      type: Number,
      default: 0
    },
    value: {
      type: Array,
      default: () => [ null, null ]
    },
    mode: {
      type: String,
      default: 'single'
    },
    hidePrevious: {
      type: Boolean,
      default: false
    },
    hideNext: {
      type: Boolean,
      default: false
    },
    /**
     * A function called for each day to determine if the day should be disabled or not.
     *
     * Type signatue (Number, Number, Number) => Boolean
     *
     * The function recieves 3 arguments in order: the year, the month, and the day of the month. The function should
     * retun true if the day should be disabled, false otherwise.
     */
    disableDayFunc: {
      type: Function
    }
  },

  data: () => ({
    dayNames
  }),

  computed: {
    humanizedMonth () { return moment().month(this.month).format('MMM') },
    daysinCurrrentMonth () {
      const {
        year,
        month
      } = this.$props

      return moment().year(year).month(month).daysInMonth()
    },
    availableDays () {
      if (this.startingDayOfWeek !== 0) {
        return Array.from(Array(this.startingDayOfWeek))
          .concat(Array.from(Array(this.daysinCurrrentMonth), (_, i) => i + 1))
      }
      return Array.from(Array(this.daysinCurrrentMonth), (_, idx) => idx + 1)
    },
    startingDayOfWeek () {
      const {
        year,
        month
      } = this.$props

      return moment().year(year).month(month).date(1).day()
    },
    startMoment () {
      /* eslint-disable */
      const [ start, __ ] = this.value
      /* eslint-enable */

      if (!start) { return null }

      // toNumber will return NaN if the value is a date string like
      // '2020-08-22'. However, if it is a string like '-90', it will return
      // -90.
      if (isNaN(toNumber(start))) {
        return moment(start)
      }

      const startNumber = toNumber(start)

      if (isNumber(startNumber) && isFinite(startNumber)) {
        if (start !== 0) {
          return moment().add(startNumber, 'days')
        } else {
          return moment()
        }
      }

      return null
    },
    endMoment () {
      /* eslint-disable */
      const [ __, end ] = this.value
      /* eslint-enable */

      if (!end) { return null }

      // See the same code in startMoment for explanation.
      if (isNaN(toNumber(end))) {
        return moment(end)
      }

      const endNumber = toNumber(end)

      if (isNumber(endNumber) && isFinite(endNumber)) {
        if (end !== 0) {
          return moment().add(endNumber, 'days')
        } else {
          return moment()
        }
      }

      return null
    }
  },

  methods: {
    dayIsDisabled (day) {
      if (!this.disableDayFunc || !day) { return false }

      return this.disableDayFunc(this.year, this.month, day)
    },
    onDayClick (day) {
      if (!day) { return }

      if (this.dayIsDisabled(day)) { return }

      const date = moment().year(this.year).month(this.month).date(day)

      if (this.mode === 'single') {
        this.$emit('input', [ date.format('YYYY-MM-DD'), date.format('YYYY-MM-DD') ])
      } else {
        this.emitUpdatedRange(date)
      }
    },
    emitUpdatedRange (date) {
      const start = this.startMoment
      const end = this.endMoment

      if (start && end) {
        this.$emit('input', [ date.format('YYYY-MM-DD'), null ])
      } else if (start && !end) {
        if (date.isBefore(start)) {
          this.$emit('input', [ date.format('YYYY-MM-DD'), start.format('YYYY-MM-DD') ])
        } else {
          this.$emit('input', [ start.format('YYYY-MM-DD'), date.format('YYYY-MM-DD') ])
        }
      } else {
        this.$emit('input', [ date.format('YYYY-MM-DD'), null ])
      }
    },
    isStart (day) {
      if (!this.startMoment) { return false }

      return this.startMoment.isSame(
        moment().year(this.year).month(this.month).date(day),
        'day'
      )
    },
    isEnd (day) {
      if (!this.endMoment) { return false }

      return this.endMoment.isSame(
        moment().year(this.year).month(this.month).date(day),
        'day'
      )
    },
    isInRange (day) {
      if (!this.startMoment || !this.endMoment) { return false }

      const d = moment().year(this.year).month(this.month).date(day)

      return this.startMoment.isBefore(d, 'day') &&
        this.endMoment.isAfter(d, 'day')
    },
    classForDay (day) {
      const isStart = this.isStart(day)
      const isEnd = this.isEnd(day)
      const isInRange = this.isInRange(day)

      if (isStart && (isEnd || this.mode === 'single')) { return [ 'bound', 'lower', 'upper' ] }

      if (isStart) {
        if (this.value[1] === null) { return [ 'bound', 'lower', 'upper' ] }
        else { return [ 'bound', 'lower' ] }
      }

      if (isEnd) { return [ 'bound', 'upper' ] }

      if (isInRange) { return [ 'selected' ] }
    }
  }
}
</script>

<style lang="scss">
@import '../../style.scss';

.nebMonth {
  box-sizing: border-box;
  overflow: hidden;
  width: 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  align-content: stretch;

  &-header {
    box-sizing: border-box;
    overflow: hidden;
    padding-bottom: 10px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 260px;

    &-prev,
    &-next {
      box-sizing: border-box;
      overflow: hidden;
      width: calc(100% / 3);
      color: $slate-grey;
      display: flex;
      align-items: center;

      .nebIcon {
        cursor: pointer;
      }
    }

    &-prev {
      justify-content: flex-start;
    }

    &-next {
      justify-content: flex-end;
    }

    &-monthName {
      width: calc(100% / 3);

      span {
        font-size: 14px;
        font-family: $defaultFont;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        color: $slate-grey;
      }
    }
  }

  &-days {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-content: flex-start;
    width: 260px;
    box-sizing: border-box;
    overflow: hidden;

    &-day {
      width: calc(100% / 7);
      height: 26px;
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 3px;

      &.disabled {
        cursor: not-allowed !important;

        >div {
          color: $warm-grey;
        }
      }

      &:not(.disabled) {
        &:hover {
          >div {
            border-radius: 50%;
            z-index: 3;
            border: solid 1px $tiffany-blue;
          }
        }
      }

      &:not(.filler) {
        cursor: pointer;
      }

      >div {
        width: 26px;
        height: 26px;
        font-size: 12px;
        font-family: $defaultFont;
        font-weight: 400;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        color: $slate-grey;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      &.filler {
        pointer-events: none;
      }

      &.bound {
        position: relative;
        z-index: 1;
        background-color: $pale-blue;

        &.lower {
          &::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 50%;
            background-color: $white;
            z-index: 2;
          }
        }

        &.upper {
          &::before {
            content: "";
            position: absolute;
            right: 0;
            top: 0;
            width: 50%;
            height: 100%;
            background-color: $white;
            z-index: 2;
          }
        }

        &.upper.lower {
          &::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 100%;
            background-color: $white;
            z-index: 2;
          }
        }

        >div {
          border-radius: 50%;
          background-color: $tiffany-blue;
          color: $white;
          z-index: 3;
        }
      }

      &.selected {
        background-color: $pale-blue;
      }
    }
  }

  &-dayNames {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 260px;
    padding-bottom: 15px;

    &-name {
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
      width: calc(100% / 7);

      span {
        font-family: $defaultFont;
        font-size: 12px;
        font-weight: 300;
        font-stretch: normal;
        font-style: normal;
        line-height: normal;
        letter-spacing: normal;
        color: $warm-grey;
      }
    }
  }
}
</style>
